<script setup lang="ts">
import { useRouter } from 'vue-router'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

interface FooterLink {
  label: string
  to: string
}

interface TrustBadge {
  icon: PiIconName
  label: string
}

const router = useRouter()
const currentYear = new Date().getFullYear()

const links: FooterLink[] = [{ label: 'Help & support', to: routes.HELP }]

// Inline regulatory badges — same trust language used inside the dashboard
// trust strip so the chrome stays consistent across the app surface.
const trust: TrustBadge[] = [
  { icon: 'verified', label: 'VARA compliant' },
  { icon: 'shield', label: 'DLD registered' },
  { icon: 'lock', label: 'Segregated funds' },
]
</script>

<template>
  <Box is="footer" class="app-footer" align="center" justify="between" gap="4">
    <Box align="center" gap="3" class="app-footer__left">
      <Text type="caption" color="muted">© {{ currentYear }} Sliceal Investments</Text>
      <Box class="app-footer__sep" />
      <Box align="center" gap="3" class="app-footer__trust">
        <Box
          v-for="badge in trust"
          :key="badge.label"
          align="center"
          gap="1"
          class="app-footer__trust-item"
        >
          <PiIcon :name="badge.icon" class="app-footer__trust-icon" />
          <Text type="caption" color="muted">{{ badge.label }}</Text>
        </Box>
      </Box>
    </Box>

    <Box align="center" gap="3" class="app-footer__links">
      <Text
        v-for="(link, index) in links"
        :key="link.label"
        is="button"
        type="caption"
        color="muted"
        weight="medium"
        class="app-footer__link"
        @click="router.push(link.to)"
      >
        {{ link.label }}<template v-if="index < links.length - 1"> ·</template>
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.app-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.app-footer__left {
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.app-footer__sep {
  width: 1px;
  height: 12px;
  background: var(--p-content-border-color);
}

.app-footer__trust {
  flex-wrap: wrap;
  row-gap: 0.25rem;
}

.app-footer__trust-icon {
  font-size: 0.75rem;
  color: var(--p-success-600);
  flex-shrink: 0;
}

.app-footer__link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.app-footer__link:hover {
  color: var(--p-text-color);
  text-decoration: underline;
}

@media (max-width: 640px) {
  .app-footer {
    padding: 0.875rem 1rem;
    flex-direction: column;
    align-items: flex-start;
  }

  .app-footer__sep {
    display: none;
  }
}
</style>
